import React, { useEffect, useRef } from 'react';
import { CLEAR_TICKS, GAP, TICK_MS, createBoard, nudge, step, tileColour, warmUp } from '../utils/tileGame';

const CELL = 26;
const CELL_SMALL = 20;
const SMALL_WIDTH = 640;
const WARM_UP_TICKS = 1500;
const RESIZE_WAIT_MS = 200;
// How quickly a drawn piece catches up with a change of column
const FOLLOW_MS = 80;
// The proportions of the tiles in the logo
const TILE = 66 / 82;
const CORNER = 16 / 66;

const ease = (t) => t * t * (3 - 2 * t);
const clamp = (t) => Math.min(1, Math.max(0, t));

const drawTile = (ctx, left, top, cell, colour, scale = 1) => {
  const size = cell * TILE * scale;
  const inset = (cell - size) / 2;
  ctx.fillStyle = colour;
  ctx.beginPath();
  ctx.roundRect(left + inset, top + inset, size, size, size * CORNER);
  ctx.fill();
};

const drawPile = (ctx, board, view, phase) => {
  const { cell, left, floor } = view;
  const leaving = new Map(board.clearing.map((entry) => [entry.row, entry]));
  board.grid.forEach((cells, row) => {
    // Rows above a clear are still coming down
    const settling = Math.max(0, board.settling[row] - phase / 3);
    const top = floor - (board.rows - row + settling) * cell;
    const entry = leaving.get(row);
    cells.forEach((colour, col) => {
      if (colour === null || colour === GAP) return;
      // A leaving row goes as a wave from the left
      const gone = entry ? clamp(((entry.age + phase) / CLEAR_TICKS) * 1.5 - (col / board.cols) * 0.5) : 0;
      if (gone < 1) drawTile(ctx, left + col * cell, top, cell, colour, 1 - ease(gone));
    });
  });
};

// Pieces change column at any moment now that the cursor can push them, so the drawing glides after them
const followPieces = (board, view, elapsed) => {
  const step = 1 - Math.exp(-elapsed / FOLLOW_MS);
  board.falling.forEach((piece) => {
    const shown = view.shownX.get(piece) ?? piece.x;
    view.shownX.set(piece, shown + (piece.x - shown) * step);
  });
};

const drawFalling = (ctx, board, view, phase) => {
  const { cell, left, floor } = view;
  const eased = ease(phase);
  board.falling.forEach((piece) => {
    const cells = piece.shape.turns[piece.turn];
    const x = left + (view.shownX.get(piece) ?? piece.x) * cell;
    const y = floor - (board.rows - (piece.fromY + (piece.y - piece.fromY) * phase)) * cell;
    ctx.save();
    if (piece.spun) {
      // Finish the quarter turn around the middle of the piece
      const middleX = x + (Math.max(...cells.map(([cx]) => cx)) + 1) * cell * 0.5;
      const middleY = y + (Math.max(...cells.map(([, cy]) => cy)) + 1) * cell * 0.5;
      ctx.translate(middleX, middleY);
      ctx.rotate(-(1 - eased) * (Math.PI / 2));
      ctx.translate(-middleX, -middleY);
    }
    cells.forEach(([cx, cy], index) => {
      drawTile(ctx, x + cx * cell, y + cy * cell, cell, tileColour(piece, index));
    });
    ctx.restore();
  });
};

/**
 * The sign-in page's backdrop: tiles in the logo's colours, a game playing itself, and the cursor
 * can push falling pieces aside. For people who ask for less motion, a still picture of a game
 * under way.
 */
const FallingTiles = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext?.('2d');
    if (!ctx) return undefined;
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    let board = null;
    let view = null;
    let frame = null;
    let lastFrame = null;
    let clock = 0;
    let waiting = null;
    // Where the cursor is in the window, null when it isn't
    let pointer = null;

    const draw = (phase) => {
      ctx.clearRect(0, 0, view.width, view.height);
      drawPile(ctx, board, view, phase);
      drawFalling(ctx, board, view, phase);
    };

    const loop = (now) => {
      // A hidden tab stops the frames; carry on from where it was rather than catching up
      const elapsed = Math.min(now - (lastFrame ?? now), TICK_MS * 2);
      lastFrame = now;
      clock += elapsed;
      if (pointer) {
        const { cell, left, floor } = view;
        nudge(board, { col: (pointer.x - left) / cell, row: board.rows - (floor - pointer.y) / cell });
      }
      while (clock >= TICK_MS) {
        step(board);
        clock -= TICK_MS;
      }
      followPieces(board, view, elapsed);
      draw(clock / TICK_MS);
      frame = requestAnimationFrame(loop);
    };

    const start = () => {
      const { innerWidth: width, innerHeight: height, devicePixelRatio: ratio = 1 } = window;
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      const cell = width < SMALL_WIDTH ? CELL_SMALL : CELL;
      const cols = Math.ceil(width / cell);
      board = createBoard(cols, Math.ceil(height / cell));
      warmUp(board, WARM_UP_TICKS);
      view = { width, height, cell, left: (width - cols * cell) / 2, floor: height, shownX: new WeakMap() };
      if (still) draw(0);
      else if (frame === null) frame = requestAnimationFrame(loop);
    };

    const handleResize = () => {
      clearTimeout(waiting);
      waiting = setTimeout(start, RESIZE_WAIT_MS);
    };

    // The canvas takes no clicks, so the cursor is followed on the window
    const handlePointer = (event) => {
      pointer = { x: event.clientX, y: event.clientY };
    };
    const handlePointerGone = () => {
      pointer = null;
    };
    // A finger is gone when it lifts; a mouse is still there
    const handlePointerUp = (event) => {
      if (event.pointerType !== 'mouse') pointer = null;
    };
    const page = document.documentElement;

    start();
    window.addEventListener('resize', handleResize);
    if (!still) {
      window.addEventListener('pointermove', handlePointer);
      window.addEventListener('pointerdown', handlePointer);
      window.addEventListener('pointerup', handlePointerUp);
      window.addEventListener('pointercancel', handlePointerGone);
      window.addEventListener('blur', handlePointerGone);
      page.addEventListener('pointerleave', handlePointerGone);
    }
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('pointermove', handlePointer);
      window.removeEventListener('pointerdown', handlePointer);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerGone);
      window.removeEventListener('blur', handlePointerGone);
      page.removeEventListener('pointerleave', handlePointerGone);
      clearTimeout(waiting);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, []);

  return <canvas ref={canvasRef} className="auth-tiles" aria-hidden="true" />;
};

export default FallingTiles;
